import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Dialog from "@/components/modals/Dialog";
import { api, del, errorMessage, post, put } from "@/lib/api";
import { accessDeniedNavigateState } from "@/utils/accessDenied";
import "./GradeRubrics.css";

const PAGE_ID = "grade-rubrics";

const TYPE_GROUPS: { label: string; types: { type: string; label: string }[] }[] = [
  {
    label: "Content",
    types: [
      { type: "reading", label: "Reading" },
      { type: "audio", label: "Audio" },
      { type: "video", label: "Video" },
      { type: "slides", label: "Slides" },
      { type: "key_terms", label: "Key terms" },
    ],
  },
  {
    label: "Graded",
    types: [
      { type: "homework", label: "Homework" },
      { type: "assessment", label: "Assessment" },
      { type: "exam", label: "Exam" },
      { type: "discussion", label: "Discussion" },
      { type: "project", label: "Project" },
      { type: "lab", label: "Lab" },
    ],
  },
];

type Measure = "points" | "minutes";

type Category = {
  label: string;
  activityTypes: string[];
  measure: Measure;
  weight: string;
  cutWeight: string;
};

type RubricCourse = {
  id: string;
  title: string;
  schoolName: string;
  canBuild: boolean;
};

type Rubric = {
  id: string;
  title: string;
  isFoundation: boolean;
  courseCount: number;
  categories: {
    label: string;
    activityTypes: string[];
    measure: Measure;
    weight: number;
    cutWeight: number | null;
  }[];
};

function blankCategory(): Category {
  return { label: "", activityTypes: [], measure: "points", weight: "", cutWeight: "" };
}

function fromRubric(rubric: Rubric): Category[] {
  return rubric.categories.map((category) => ({
    label: category.label,
    activityTypes: [...category.activityTypes],
    measure: category.measure,
    weight: String(category.weight),
    cutWeight: category.cutWeight == null ? "" : String(category.cutWeight),
  }));
}

function toPayload(categories: Category[]) {
  return categories.map((category) => ({
    label: category.label,
    activityTypes: category.activityTypes,
    measure: category.measure,
    weight: category.weight === "" ? 0 : Number(category.weight),
    cutWeight: category.cutWeight === "" ? null : Number(category.cutWeight),
  }));
}

export default function GradeRubrics() {
  const { pageAccess, loading, session } = useAuth();
  const allowed = Boolean(pageAccess?.[PAGE_ID]);
  const canOpenBuilder = Boolean(pageAccess?.["course-builder"]);
  const actions = session?.acuityLearn?.gradeRubricActions;
  const canCreate = actions?.create === true;
  const canEdit = actions?.edit === true;
  const canDelete = actions?.delete === true;
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [isFoundation, setIsFoundation] = useState(true);
  const [categories, setCategories] = useState<Category[]>([blankCategory()]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmSave, setConfirmSave] = useState(false);
  const [coursesOpen, setCoursesOpen] = useState(false);
  const [coursesFor, setCoursesFor] = useState<string | null>(null);
  const [courses, setCourses] = useState<RubricCourse[] | null>(null);
  const [coursesError, setCoursesError] = useState("");

  const selected = rubrics.find((row) => row.id === selectedId) || null;

  const load = async (preferId?: string | null) => {
    const envelope = await api<{ success: boolean; data: Rubric[] }>("/api/grade-rubrics");
    const rows = Array.isArray(envelope.data) ? envelope.data : [];
    setRubrics(rows);
    const next = preferId && rows.some((row) => row.id === preferId) ? preferId : rows[0]?.id || null;
    setSelectedId(next);
    setCreating(false);
    return rows;
  };

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    (async () => {
      try {
        await load();
      } catch (err) {
        if (!cancelled) setError(errorMessage(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed]);

  useEffect(() => {
    if (creating || !selected) return;
    setTitle(selected.title);
    setCategories(selected.categories.length ? fromRubric(selected) : [blankCategory()]);
    setConfirmSave(false);
    setError("");
  }, [creating, selected]);

  if (loading) return null;
  if (!allowed) {
    return <Navigate to="/" replace state={accessDeniedNavigateState(PAGE_ID, "/grade-rubrics")} />;
  }

  const startNew = () => {
    setCreating(true);
    setSelectedId(null);
    setTitle("");
    setIsFoundation(true);
    setCategories([blankCategory()]);
    setConfirmSave(false);
    setError("");
  };

  const patchCategory = (index: number, patch: Partial<Category>) => {
    setCategories((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    setConfirmSave(false);
  };

  const toggleType = (index: number, type: string) => {
    setCategories((rows) =>
      rows.map((row, i) => {
        if (i !== index) return row;
        const has = row.activityTypes.includes(type);
        return {
          ...row,
          activityTypes: has ? row.activityTypes.filter((item) => item !== type) : [...row.activityTypes, type],
        };
      }),
    );
    setConfirmSave(false);
  };

  const save = async () => {
    if (selected && selected.courseCount > 0 && !confirmSave) {
      setConfirmSave(true);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const body = { title, isFoundation, categories: toPayload(categories) };
      const envelope = creating
        ? await post<{ success: boolean; data: Rubric }>("/api/grade-rubrics", body)
        : await put<{ success: boolean; data: Rubric }>(`/api/grade-rubrics/${selectedId}`, body);
      await load(envelope.data.id);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
      setConfirmSave(false);
    }
  };

  const copy = async () => {
    if (!selectedId) return;
    setBusy(true);
    setError("");
    try {
      const envelope = await post<{ success: boolean; data: Rubric }>(`/api/grade-rubrics/${selectedId}/copy`, {});
      await load(envelope.data.id);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleCourses = async (rubricId: string) => {
    setCreating(false);
    setSelectedId(rubricId);
    if (coursesOpen && coursesFor === rubricId) {
      setCoursesOpen(false);
      return;
    }
    setCoursesOpen(true);
    setCoursesFor(rubricId);
    setCourses(null);
    setCoursesError("");
    try {
      const envelope = await api<{ success: boolean; data: RubricCourse[] }>(
        `/api/grade-rubrics/${encodeURIComponent(rubricId)}/courses`,
      );
      setCourses(Array.isArray(envelope.data) ? envelope.data : []);
    } catch (err) {
      setCourses([]);
      setCoursesError(errorMessage(err));
    }
  };

  const remove = async () => {
    if (!selectedId || (selected && selected.courseCount > 0)) return;
    setBusy(true);
    setError("");
    try {
      await del(`/api/grade-rubrics/${selectedId}`);
      await load(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const courseCount = selected?.courseCount || 0;

  return (
    <div className="al-rubrics">
      <header className="al-rubrics-head">
        <h1>Grade rubrics</h1>
        <p>A foundation is shared. Copy it when one course needs its own weights.</p>
      </header>
      <div className="al-rubrics-layout">
        <div className="al-rubrics-list">
          {canCreate ? (
            <button type="button" className="al-rubrics-new" onClick={startNew}>
              New rubric
            </button>
          ) : null}
          {rubrics.map((rubric) => (
            <div
              key={rubric.id}
              className={`al-rubrics-list-row${rubric.id === selectedId && !creating ? " is-on" : ""}`}
            >
              <button
                type="button"
                className="al-rubrics-list-pick"
                onClick={() => {
                  setCreating(false);
                  setSelectedId(rubric.id);
                  setCoursesOpen(false);
                }}
              >
                <span className="al-rubrics-list-title">{rubric.title || "Untitled"}</span>
              </button>
              <button
                type="button"
                className="al-rubrics-list-meta"
                disabled={rubric.courseCount === 0}
                  aria-haspopup="dialog"
                  aria-expanded={coursesOpen && coursesFor === rubric.id}
                onClick={() => void toggleCourses(rubric.id)}
              >
                {rubric.isFoundation ? "Foundation" : "Copy"} · {rubric.courseCount}
              </button>
            </div>
          ))}
        </div>
        <form
          className="al-rubrics-editor"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <div className="al-rubrics-title-row">
            <label className="al-rubrics-title">
              Title
              <input value={title} onChange={(event) => { setTitle(event.target.value); setConfirmSave(false); }} />
            </label>
            {creating ? (
              <label className="al-rubrics-check">
                <input
                  type="checkbox"
                  checked={isFoundation}
                  onChange={(event) => setIsFoundation(event.target.checked)}
                />
                Foundation
              </label>
            ) : (
              <p className="al-rubrics-note">
                {selected?.isFoundation ? "Foundation" : "Copy"}
                {" · "}
                <button
                  type="button"
                  className="al-rubrics-count"
                  disabled={courseCount === 0 || !selected}
                  aria-haspopup="dialog"
                  aria-expanded={Boolean(selected && coursesOpen && coursesFor === selected.id)}
                  onClick={() => selected && void toggleCourses(selected.id)}
                >
                  {courseCount} {courseCount === 1 ? "course" : "courses"}
                </button>
              </p>
            )}
          </div>
          {categories.map((category, index) => (
            <div className="al-rubrics-line" key={index}>
              <div className="al-rubrics-line-grid">
                <label>
                  Label
                  <input
                    type="text"
                    value={category.label}
                    onChange={(event) => patchCategory(index, { label: event.target.value })}
                  />
                </label>
                <label>
                  Measure
                  <select
                    value={category.measure}
                    onChange={(event) => patchCategory(index, { measure: event.target.value as Measure })}
                  >
                    <option value="points">Points</option>
                    <option value="minutes">Minutes</option>
                  </select>
                </label>
                <label>
                  Weight
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={category.weight}
                    onChange={(event) => patchCategory(index, { weight: event.target.value })}
                  />
                </label>
                <label>
                  Cut
                  <input
                    type="number"
                    min={0}
                    placeholder="No ceiling"
                    value={category.cutWeight}
                    onChange={(event) => patchCategory(index, { cutWeight: event.target.value })}
                  />
                </label>
                <button
                  type="button"
                  className="al-rubrics-remove"
                  onClick={() => setCategories((rows) => rows.filter((_, i) => i !== index))}
                  disabled={categories.length === 1}
                >
                  Remove
                </button>
              </div>
              <div className="al-rubrics-types">
                {TYPE_GROUPS.map((group) => (
                  <div key={group.label} className="al-rubrics-type-group">
                    <span>{group.label}</span>
                    {group.types.map((type) => (
                      <button
                        key={type.type}
                        type="button"
                        className={category.activityTypes.includes(type.type) ? "is-on" : ""}
                        onClick={() => toggleType(index, type.type)}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="al-rubrics-actions">
            <button type="button" onClick={() => setCategories((rows) => [...rows, blankCategory()])}>
              Add category
            </button>
            {(creating ? canCreate : canEdit) ? (
              <button type="submit" className="is-primary" disabled={busy}>
                {confirmSave ? `Save for ${courseCount} ${courseCount === 1 ? "course" : "courses"}` : "Save"}
              </button>
            ) : null}
            {!creating && selected && canCreate ? (
              <button type="button" onClick={() => void copy()} disabled={busy}>
                Copy
              </button>
            ) : null}
            {!creating && selected && canDelete ? (
              <button type="button" onClick={() => void remove()} disabled={busy || courseCount > 0}>
                {courseCount > 0 ? "In use" : "Delete"}
              </button>
            ) : null}
          </div>
          {confirmSave ? (
            <p className="al-rubrics-note">Saving changes the grade mix for every course still on this rubric.</p>
          ) : null}
          {error ? <p className="al-rubrics-error">{error}</p> : null}
        </form>
      </div>
      <Dialog
        open={coursesOpen}
        title={rubrics.find((row) => row.id === coursesFor)?.title || "Courses"}
        subtitle="Courses on this rubric"
        onClose={() => setCoursesOpen(false)}
        width="22rem"
      >
        {coursesError ? (
          <p className="al-rubrics-error">{coursesError}</p>
        ) : courses == null ? (
          <p className="al-rubrics-course-muted">Loading courses…</p>
        ) : courses.length === 0 ? (
          <p className="al-rubrics-course-muted">No courses use this rubric.</p>
        ) : (
          <ul className="al-rubrics-courses">
            {courses.map((course) => (
              <li key={course.id}>
                {canOpenBuilder && course.canBuild ? (
                  <Link to={`/courses/${encodeURIComponent(course.id)}/build`}>{course.title}</Link>
                ) : (
                  <span>{course.title}</span>
                )}
                <span className="al-rubrics-course-school">{course.schoolName}</span>
              </li>
            ))}
          </ul>
        )}
      </Dialog>
    </div>
  );
}
